import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { ROUTES } from '@metal/shared'
import { Avatar, Icon } from '@metal/ui'
import { useSession } from '@metal/ui/session'

export function AccountMenu({ compact = false }: { compact?: boolean }) {
  const { user, signOut } = useSession()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false)
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  if (!user) return null

  // The person, and under it the company they act for. A company registered
  // through the full form carries the company as the account holder's name
  // too, so the second line appears only when it says something new.
  const company =
    user.partyKind === 'company' &&
    user.shortName !== user.personName &&
    user.displayName !== user.personName
      ? user.shortName
      : null

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        className="row"
        style={{ gap: 8, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={compact ? 'حساب کاربری' : undefined}
        onClick={() => setOpen((v) => !v)}
      >
        <Avatar name={user.avatarLetter ?? user.shortName} size={32} />
        {!compact && (
          <>
            <span className="col acct-name" style={{ gap: 0, lineHeight: 1.35, alignItems: 'flex-start' }}>
              <span className="t-cap t-sb">{user.personName}</span>
              {company && <span className="t-micro t-muted">{company}</span>}
            </span>
            <Icon name="chevron-down" size="sm" />
          </>
        )}
      </button>

      {open && (
        <div
          role="menu"
          className="card card-float"
          style={{
            position: 'absolute',
            insetBlockStart: 'calc(100% + 8px)',
            insetInlineEnd: 0,
            inlineSize: 232,
            padding: 6,
            zIndex: 60,
          }}
        >
          <MenuLink to={ROUTES.dashboard()} icon="grid" label="داشبورد" onGo={() => setOpen(false)} />
          <MenuLink
            to={ROUTES.settingsProfile()}
            icon="building"
            label={user.partyKind === 'person' ? 'حساب کاربری' : 'اطلاعات شرکت'}
            onGo={() => setOpen(false)}
          />
          <MenuLink to={ROUTES.grade()} icon="medal" label="گرید و کارنامه" onGo={() => setOpen(false)} />
          <MenuLink to={ROUTES.documents()} icon="doc" label="مدارک" onGo={() => setOpen(false)} />
          <MenuLink to={ROUTES.news()} icon="message" label="اخبار" onGo={() => setOpen(false)} />
          <MenuLink
            to={ROUTES.settingsNotifications()}
            icon="bell"
            label="تنظیمات اعلان‌ها"
            onGo={() => setOpen(false)}
          />
          <div className="rule" style={{ marginBlock: 6 }} />
          <button
            type="button"
            role="menuitem"
            className="row"
            style={{
              gap: 10,
              inlineSize: '100%',
              blockSize: 34,
              paddingInline: 10,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: 'var(--fs-sm)',
              color: 'var(--c-danger)',
              borderRadius: 'var(--r-sm)',
            }}
            onClick={() => void signOut()}
          >
            <Icon name="logout" size="sm" />
            خروج
          </button>
        </div>
      )}
    </div>
  )
}

function MenuLink({
  to,
  icon,
  label,
  onGo,
}: {
  to: string
  icon: Parameters<typeof Icon>[0]['name']
  label: string
  onGo: () => void
}) {
  return (
    <Link
      role="menuitem"
      to={to}
      onClick={onGo}
      className="row"
      style={{
        gap: 10,
        blockSize: 34,
        paddingInline: 10,
        fontSize: 'var(--fs-sm)',
        color: 'var(--c-fg-secondary)',
        borderRadius: 'var(--r-sm)',
      }}
    >
      <Icon name={icon} size="sm" />
      {label}
    </Link>
  )
}
